import { useAppSelector } from 'redux-stores';
import { useGetTasksQuery } from 'redux-stores/tasks/tasksApi';
import type { Task } from 'js-sdk/src/types/Tasks';

export type MessageTaskState = {
  /** The task linked to this message, if any. */
  task: Task | undefined;
  /** Whether a task already exists for this message (drives affordance state). */
  hasTask: boolean;
  /** True while the initial tasks fetch is in flight. */
  isLoading: boolean;
};

/**
 * Returns the task linked to a message. Derives from RTK Query data directly
 * to avoid a race between isLoading→false and the onQueryStarted side-effect
 * that populates linkedTasksByMessageId. Session-local mutations (create/
 * complete/reopen) are still reflected via the slice.
 */
export function useMessageTask(messageId: string, organizationId: string): MessageTaskState {
  const { data: openTasks, isLoading: isOpenLoading } = useGetTasksQuery({
    organizationId,
    filter: 'open',
  });
  const { data: completedTasks, isLoading: isCompletedLoading } = useGetTasksQuery({
    organizationId,
    filter: 'completed',
  });
  const isLoading = isOpenLoading || isCompletedLoading;

  // Prefer session-local task (latest mutation), fall back to fetched list.
  const sessionTask = useAppSelector((state) => state.tasks.linkedTasksByMessageId[messageId]);
  const fetchedTask = [...(openTasks ?? []), ...(completedTasks ?? [])].find(
    (t) => t.source_type === 'message' && t.source_ref?.message_id === messageId
  );
  const task = sessionTask ?? fetchedTask;

  return { task, hasTask: !!task, isLoading };
}
